import MuGrid, { GridProps as MuGridProps, GridSpacing } from '@material-ui/core/Grid';
import React, { forwardRef, useEffect, useMemo, useState } from 'react';

import { useBreakpoints } from 'hooks/useBreakpoints';
import { GRID_TEST_ID } from './Grid.const';

export enum GridGutter {
  mobileOrTablet = 4,
  desktop = 6,
  desktopLarge = 8,
}

export interface IGridProps extends MuGridProps {
  gutter?: boolean;
}

export const Grid = forwardRef<HTMLDivElement, IGridProps>((props, ref) => {
  const { gutter, spacing: defaultSpacing, container: defaultContainer, ...gridProps } = props;
  const { browser } = useBreakpoints();
  const isDesktop = browser.is.desktop && !browser.greaterThan.desktop;
  const isDesktopLarge = browser.greaterThan.desktop;

  const [spacing, setSpacing] = useState<GridSpacing | undefined>(
    getSpacing(isDesktop, isDesktopLarge, gutter, defaultSpacing),
  );

  useEffect(() => {
    const newSpacing = getSpacing(isDesktop, isDesktopLarge, gutter, defaultSpacing);
    if (newSpacing !== spacing) setSpacing(newSpacing);
  }, [isDesktop, isDesktopLarge, gutter, defaultSpacing]);

  const container = spacing !== undefined ? true : defaultContainer;

  return useMemo(
    () => (
      <MuGrid
        container={container}
        data-testid={GRID_TEST_ID.grid}
        ref={ref}
        {...gridProps}
        spacing={spacing}
      />
    ),
    [props, spacing],
  );
});

Grid.displayName = 'Grid';

const getSpacing = (
  isDesktop: boolean,
  isDesktopLarge: boolean,
  gutter?: boolean,
  spacing?: GridSpacing,
): GridSpacing | undefined => {
  if (!gutter) return spacing;

  if (isDesktopLarge) return GridGutter.desktopLarge;
  if (isDesktop) return GridGutter.desktop;
  return GridGutter.mobileOrTablet;
};
